import { useState, useCallback } from 'react';
import { CartItem, CartProduct } from '../QuickSaleScreen';

interface UseQuickSaleCartReturn {
  // State
  cart: CartItem[];
  selectedCartItemId: string | null;
  
  // Actions
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  setSelectedCartItemId: React.Dispatch<React.SetStateAction<string | null>>;
  addToCart: (product: CartProduct, quantity?: number) => void;
  updateQuantity: (cartItemId: string, change: number) => void; // ✅ cartItemId kullan
  removeFromCart: (cartItemId: string) => void; // ✅ cartItemId kullan
  clearCart: () => void;
  handleSelectItem: (cartItemId: string) => void; // ✅ cartItemId kullan
  handleIncreaseSelected: () => void;
  handleDecreaseSelected: () => void;
  handleDeleteSelected: () => void;
  calculateTotal: () => number;
}

/**
 * Hızlı satış sepet hook'u.
 *
 * @param forceSeparateLines Aynı ürün tekrar eklendiğinde quantity artırma
 *  yerine yeni satır olarak eklenir. Restoran modu (her sipariş kaleminin
 *  bireysel takibi için) ve Paket Hızlı Satış modu (PackageOrderScreen'a ayrı
 *  kalemler olarak aktarmak için) bu flag'i `true` olarak geçer. Default
 *  davranış (false) "üst üste birleştir" olur.
 */
export const useQuickSaleCart = (forceSeparateLines: boolean = false): UseQuickSaleCartReturn => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCartItemId, setSelectedCartItemId] = useState<string | null>(null);

  // Sepete ürün ekleme fonksiyonu
  const addToCart = useCallback((product: CartProduct, quantity: number = 1) => {
    console.log('🔵 [CART HOOK] addToCart çağrıldı', {
      productId: product.id,
      productName: product.name,
      quantity,
      forceSeparateLines,
    });

    setCart((prevCart) => {
      // Ayrı satır modu: her ekleme yeni satır olarak gider (restoran + paket).
      if (forceSeparateLines) {
        const uniqueCartItemId = `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return [...prevCart, { 
          ...product, 
          quantity,
          cartItemId: uniqueCartItemId
        }];
      }

      const existingItemIndex = prevCart.findIndex((item) => item.id === product.id);
      
      if (existingItemIndex > -1) {
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += quantity;
        return updatedCart;
      } else {
        const uniqueCartItemId = `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return [...prevCart, { 
          ...product, 
          quantity,
          cartItemId: uniqueCartItemId
        }];
      }
    });
  }, [forceSeparateLines]);

  // Miktar güncelleme fonksiyonu - ✅ cartItemId kullan
  const updateQuantity = useCallback((cartItemId: string, change: number) => {
    console.log('🔵 [CART HOOK] updateQuantity çağrıldı', { 
      cartItemId, 
      change
    });
    
    setCart((prevCart) => {
      console.log('🔵 [CART HOOK] Sepet güncelleniyor...', {
        prevCartLength: prevCart.length,
        cartItemIds: prevCart.map(i => i.cartItemId)
      });
      
      const itemToUpdate = prevCart.find(item => item.cartItemId === cartItemId);
      console.log('🔍 [CART HOOK] Güncellenecek item:', itemToUpdate);
      
      return prevCart
        .map((item) => {
          if (item.cartItemId === cartItemId) {
            const newQuantity = item.quantity + change;
            console.log('✅ [CART HOOK] Item bulundu, miktar güncelleniyor:', {
              oldQuantity: item.quantity,
              change,
              newQuantity
            });
            
            // Eğer miktar 0 veya negatif olursa ürünü sepetten kaldır
            if (newQuantity <= 0) {
              console.log('⚠️ [CART HOOK] Miktar 0 veya negatif, item siliniyor');
              return null;
            }
            
            return { ...item, quantity: newQuantity };
          }
          return item;
        })
        .filter((item): item is CartItem => item !== null);
    });
  }, []); // ✅ Dependency array boş - setCart zaten güncel prevCart kullanıyor

  // Sepetten ürün kaldırma - ✅ cartItemId kullan
  const removeFromCart = useCallback((cartItemId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.cartItemId !== cartItemId));
    
    // Eğer silinen ürün seçiliyse, seçimi kaldır
    setSelectedCartItemId((prevId) => (prevId === cartItemId ? null : prevId));
  }, []);

  // Sepeti temizleme
  const clearCart = useCallback(() => {
    setCart([]);
    setSelectedCartItemId(null);
  }, []);

  // Sepet elemanı seçme (toggle)
  const handleSelectItem = useCallback((itemId: string) => {
    console.log('🔵 [CART HOOK] handleSelectItem çağrıldı', { 
      itemId,
      currentSelectedId: selectedCartItemId
    });
    
    setSelectedCartItemId((prevId) => {
      const newId = prevId === itemId ? null : itemId;
      console.log('✅ [CART HOOK] Seçim güncellendi:', { 
        oldSelection: prevId, 
        newSelection: newId 
      });
      return newId;
    });
  }, [selectedCartItemId]);

  // Seçili ürünü artırma
  const handleIncreaseSelected = useCallback(() => {
    console.log('🔵 [CART HOOK] handleIncreaseSelected çağrıldı', { selectedCartItemId });
    if (selectedCartItemId) {
      console.log('✅ [CART HOOK] updateQuantity çağrılıyor (+1)');
      updateQuantity(selectedCartItemId, 1);
    } else {
      console.log('⚠️ [CART HOOK] selectedCartItemId NULL, işlem yapılmadı');
    }
  }, [selectedCartItemId, updateQuantity]);

  // Seçili ürünü azaltma
  const handleDecreaseSelected = useCallback(() => {
    console.log('🔵 [CART HOOK] handleDecreaseSelected çağrıldı', { selectedCartItemId });
    if (selectedCartItemId) {
      console.log('✅ [CART HOOK] updateQuantity çağrılıyor (-1)');
      updateQuantity(selectedCartItemId, -1);
    } else {
      console.log('⚠️ [CART HOOK] selectedCartItemId NULL, işlem yapılmadı');
    }
  }, [selectedCartItemId, updateQuantity]);

  // Seçili ürünü silme
  const handleDeleteSelected = useCallback(() => {
    console.log('🔵 [CART HOOK] handleDeleteSelected çağrıldı', { selectedCartItemId });
    if (selectedCartItemId) {
      console.log('✅ [CART HOOK] removeFromCart çağrılıyor');
      removeFromCart(selectedCartItemId);
    } else {
      console.log('⚠️ [CART HOOK] selectedCartItemId NULL, işlem yapılmadı');
    }
  }, [selectedCartItemId, removeFromCart]);

  // Sepet toplamını hesaplama (KDV dahil)
  // ⚠️ TERMİNOLOJİ UYARISI: AddInventoryItem.tsx'te terminoloji TERS kullanılmış!
  // Kullanıcı "KDV Dahil" seçince → veritabanına 'included' kaydediliyor
  // Kullanıcı "KDV Hariç" seçince → veritabanına 'excluded' kaydediliyor
  // Bu yüzden hızlı satışta TERSİNİ uyguluyoruz:
  // vatStatus === 'included' → Fiyat KDV DAHİL → KDV eklenmeyecek
  // vatStatus === 'excluded' → Fiyat KDV HARİÇ → KDV eklenecek
  const calculateTotal = useCallback(() => {
    // ⚠️ PERFORMANS: Bu fonksiyon her render'da defalarca çağrılıyor (header, footer,
    // payment panel, customer display, useEffect bağımlılıkları vb). 60+ kalemli
    // sepetlerde her çağrı için kalem-bazlı `console.log` UI'ı kilitliyordu
    // (60 kalem × 10-15 çağrı/render = 600-900 log/render).
    // Per-item debug logu varsayılan olarak KAPALI; sorun teşhisi için
    // window.__GTS_DEBUG_TOTAL = true ile geçici olarak açılabilir.
    const debugEnabled =
      typeof window !== 'undefined' && (window as any).__GTS_DEBUG_TOTAL === true;
    if (debugEnabled) {
      let debugTotal = 0;
      cart.forEach((item, index) => {
        const itemTotal = item.price * item.quantity;
        let itemFinal = itemTotal;

        if (item.vatStatus === 'included') {
          console.log(`🧾 [TOPLAM DEBUG] ${index + 1}. ${item.name}: ${item.price} x ${item.quantity} = ${itemTotal} TL (vatStatus: ${item.vatStatus} → KDV DAHİL, KDV EKLENMEDİ)`);
        } else {
          const vatAmount = itemTotal * ((item.vatRate || 0) / 100);
          itemFinal = itemTotal + vatAmount;
          console.log(`🧾 [TOPLAM DEBUG] ${index + 1}. ${item.name}: ${item.price} x ${item.quantity} = ${itemTotal} + ${vatAmount.toFixed(2)} KDV = ${itemFinal.toFixed(2)} TL (vatStatus: ${item.vatStatus} → KDV HARİÇ, KDV EKLENDİ)`);
        }
        debugTotal += itemFinal;
      });
      console.log(`🧾 [TOPLAM DEBUG] GENEL TOPLAM: ${debugTotal.toFixed(2)} TL`);
    }

    return cart.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      
      if (item.vatStatus === 'included') {
        return total + itemTotal;
      } else {
        const vatAmount = itemTotal * ((item.vatRate || 0) / 100);
        return total + itemTotal + vatAmount;
      }
    }, 0);
  }, [cart]);

  return {
    cart,
    selectedCartItemId,
    setCart,
    setSelectedCartItemId,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    handleSelectItem,
    handleIncreaseSelected,
    handleDecreaseSelected,
    handleDeleteSelected,
    calculateTotal,
  };
};

